// Client layer (TS-2.5.03). Add, edit and delete against a project's notes (risks/decisions).
// Unlike milestones, ProjectDetail.notes is NoteRecord[] - the RAW record (ProjectDetail.cs) - so
// an edit here submits back the same shape it read, and cannot silently drop a field the way the
// milestones section's View-only data would (see MilestonesSection.tsx's header).
// Type and Status are the two of this form's fields the owner ruled CLOSED (2026-10-03): each
// renders as a `VocabularySelect` rather than the old `<input list>` + `<datalist>` pair - see
// that component's header for why, and for the "(not in list)" handling of a stored value outside
// the active vocabulary.
import { useState } from "react";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { VocabularySelect } from "../../components/VocabularySelect";
import type { NoteRecord, ReferenceValue } from "../../api/generated/contracts";
import { portfolioDataApi } from "../../api/portfolioData";
import { strings } from "../../strings";

export interface NotesSectionProps {
  readonly projectKey: string;
  readonly notes: readonly NoteRecord[];
  readonly typeOptions: readonly ReferenceValue[];
  readonly statusOptions: readonly ReferenceValue[];
  readonly readOnly: boolean;
  readonly onChanged: () => void;
}

const S = strings.projects.notes;

interface NoteDraft {
  readonly rawType: string;
  readonly text: string;
  readonly rawStatus: string;
}

const EMPTY_DRAFT: NoteDraft = { rawType: "", text: "", rawStatus: "" };

function draftFromNote(note: NoteRecord): NoteDraft {
  return { rawType: note.rawType ?? "", text: note.text ?? "", rawStatus: note.rawStatus ?? "" };
}

interface NoteFieldsProps {
  readonly idPrefix: string;
  readonly draft: NoteDraft;
  readonly onChange: (patch: Partial<NoteDraft>) => void;
  readonly typeOptions: readonly ReferenceValue[];
  readonly statusOptions: readonly ReferenceValue[];
}

function NoteFields({ idPrefix, draft, onChange, typeOptions, statusOptions }: NoteFieldsProps) {
  return (
    <>
      <FormField id={`${idPrefix}-type`} label={S.fields.type} required>
        <VocabularySelect
          id={`${idPrefix}-type`}
          value={draft.rawType}
          onChange={(next) => onChange({ rawType: next })}
          options={typeOptions}
          required
        />
      </FormField>
      <FormField id={`${idPrefix}-text`} label={S.fields.text} required>
        <textarea
          required
          value={draft.text}
          onChange={(event) => onChange({ text: event.target.value })}
          {...formFieldControlProps(`${idPrefix}-text`)}
        />
      </FormField>
      <FormField id={`${idPrefix}-status`} label={S.fields.status}>
        <VocabularySelect
          id={`${idPrefix}-status`}
          value={draft.rawStatus}
          onChange={(next) => onChange({ rawStatus: next })}
          options={statusOptions}
        />
      </FormField>
    </>
  );
}

export function NotesSection({ projectKey, notes, typeOptions, statusOptions, readOnly, onChanged }: NotesSectionProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [addDraft, setAddDraft] = useState<NoteDraft>(EMPTY_DRAFT);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<NoteDraft>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);

  async function addNote() {
    setBusy(true);
    try {
      await portfolioDataApi.createNote({
        note: {
          key: null,
          businessProjectId: null,
          projectKey,
          rawType: addDraft.rawType,
          text: addDraft.text,
          rawStatus: addDraft.rawStatus.length > 0 ? addDraft.rawStatus : null,
          rawNoteDate: null,
          rawSeverity: null,
          rawUrgency: null,
          owner: null,
          rawNeededBy: null,
          concurrencyToken: null,
        },
        concurrencyToken: null,
      });
      setAddDraft(EMPTY_DRAFT);
      setShowAdd(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(note: NoteRecord) {
    if (!note.key) return;
    setBusy(true);
    try {
      await portfolioDataApi.updateNote(note.key, {
        note: {
          ...note,
          rawType: editDraft.rawType,
          text: editDraft.text,
          rawStatus: editDraft.rawStatus.length > 0 ? editDraft.rawStatus : null,
        },
        concurrencyToken: note.concurrencyToken,
      });
      setEditingKey(null);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function deleteNote(noteKey: string) {
    setBusy(true);
    try {
      await portfolioDataApi.deleteNote(noteKey);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="eg-notes eg-project-detail__card" aria-labelledby="eg-notes-heading">
      <h2 id="eg-notes-heading">{S.heading}</h2>

      {notes.length === 0 ? (
        <p>{S.empty}</p>
      ) : (
        <ul className="eg-notes__list">
          {notes.map((note) => (
            <li key={note.key ?? note.text} className="eg-notes__item">
              {editingKey === note.key && note.key ? (
                <form
                  className="eg-notes__edit-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void saveEdit(note);
                  }}
                >
                  <NoteFields
                    idPrefix={`eg-note-edit-${note.key}`}
                    draft={editDraft}
                    onChange={(patch) => setEditDraft((current) => ({ ...current, ...patch }))}
                    typeOptions={typeOptions}
                    statusOptions={statusOptions}
                  />
                  <div className="eg-project-detail__actions">
                    <button type="submit" className="eg-project-detail__btn--primary" disabled={busy}>
                      {strings.projects.detail.save}
                    </button>
                    <button type="button" disabled={busy} onClick={() => setEditingKey(null)}>
                      {strings.projects.detail.cancel}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <span className="eg-notes__type">{note.rawType ?? "—"}</span>
                  <span className="eg-notes__text">{note.text}</span>
                  <span className="eg-notes__status">{note.rawStatus ?? "—"}</span>
                  {!readOnly && note.key && (
                    <span className="eg-notes__actions">
                      <button
                        type="button"
                        onClick={() => {
                          setEditDraft(draftFromNote(note));
                          setEditingKey(note.key);
                        }}
                      >
                        {S.edit}
                      </button>
                      <button
                        type="button"
                        className="eg-project-detail__btn--danger"
                        disabled={busy}
                        onClick={() => void deleteNote(note.key ?? "")}
                      >
                        {S.delete}
                      </button>
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <>
          {!showAdd ? (
            <button type="button" onClick={() => setShowAdd(true)}>
              {S.add}
            </button>
          ) : (
            <form
              className="eg-notes__add-form"
              onSubmit={(event) => {
                event.preventDefault();
                void addNote();
              }}
            >
              <NoteFields
                idPrefix="eg-note-add"
                draft={addDraft}
                onChange={(patch) => setAddDraft((current) => ({ ...current, ...patch }))}
                typeOptions={typeOptions}
                statusOptions={statusOptions}
              />
              <div className="eg-project-detail__actions">
                <button type="submit" className="eg-project-detail__btn--primary" disabled={busy}>
                  {S.add}
                </button>
                <button type="button" disabled={busy} onClick={() => setShowAdd(false)}>
                  {strings.projects.detail.cancel}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}
